import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { PopupContainer } from "@/components/layout/PopupContainer";
import { AchievementBadgePlaceholder } from "@/components/icons/AchievementBadgePlaceholder";
import { DefaultContentContainer } from "../../../../shared/DefaultContainer/DefaultContentContainer";
import { useActiveOverlay, useUnlockedAchievements } from "@/state/selectors";
import { useAchievements, useCacheStatus, useGameConfiguration } from "@/state/cacheStoreHooks";
import { useGameStore } from "@/state/gameStore";
import { OverlayType } from "@/types/state";
import type { StrapiAsset } from "@/types/api";
import {StrapiMedia} from "@/components/shared/StrapiMedia";

interface AchievementsProps {
    onClose?: () => void;
}

type AchievementDisplayItem = {
    documentId: string;
    name: string;
    image: StrapiAsset | null;
    earned: boolean;
};

export const Achievements: React.FC<AchievementsProps> = ({ onClose }) => {
    const activeOverlay = useActiveOverlay();
    const setOverlay = useGameStore((state) => state.setOverlay);
    const achievementsFromStore = useAchievements();
    const gameConfig = useGameConfiguration();
    const unlockedAchievements = useUnlockedAchievements();
    const { status } = useCacheStatus();

    const isOpen = activeOverlay === OverlayType.Achievements;
    const isLoading = status === "idle" || status === "loading";

    const scrollRef = useRef<HTMLDivElement>(null);
    const [maskHeight, setMaskHeight] = useState(0);
    const MAX_MASK_HEIGHT = 32;

    const updateScrollState = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        setMaskHeight(Math.min(el.scrollTop, MAX_MASK_HEIGHT));
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        updateScrollState();
        const observer = new ResizeObserver(updateScrollState);
        observer.observe(el);
        return () => observer.disconnect();
    }, [updateScrollState]);

    const achievementOrder = useMemo(
        () => gameConfig?.AchievementOrder?.map((a) => a?.documentId).filter((id): id is string => !!id) ?? [],
        [gameConfig?.AchievementOrder],
    );

    const achievementsById = useMemo(() => {
        const map = new Map<string, NonNullable<(typeof achievementsFromStore)[number]>>();
        for (const a of achievementsFromStore) {
            if (a != null) map.set(a.documentId, a);
        }
        return map;
    }, [achievementsFromStore]);

    const displayItems: AchievementDisplayItem[] = useMemo(() => {
        return achievementOrder.map((documentId) => {
            const a = achievementsById.get(documentId);
            const earned = unlockedAchievements.includes(documentId);

            return {
                documentId,
                name: a?.name ?? "",
                image: earned ? a?.image! : null,
                earned,
            };
        });
    }, [achievementOrder, achievementsById, unlockedAchievements]);

    const handleClose = () => {
        setOverlay(OverlayType.None);
    };

    const handleExitComplete = () => {
        onClose?.();
    };

    return (
        <AnimatePresence onExitComplete={handleExitComplete}>
            {isOpen && (
                <PopupContainer
                    key="achievements-popup"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    title="Trophäen"
                    onClose={handleClose}
                >
                    {isLoading ? (
                        <div className="text-white text-sm text-center py-8">Laden...</div>
                    ) : (
                        <div
                            ref={scrollRef}
                            onScroll={updateScrollState}
                            className="w-full overflow-y-auto overflow-x-hidden pb-6"
                            style={{
                                maxHeight: "490px",
                                scrollbarWidth: "none",
                                maskImage:
                                    maskHeight > 0
                                        ? `linear-gradient(to bottom, transparent, black ${maskHeight}px)`
                                        : undefined,
                            }}
                            aria-label="Achievements list"
                        >
                            <div className="grid grid-cols-3 gap-2 w-full justify-items-center">
                                {displayItems.map((achievement) => {
                                    const cellClassName =
                                        "flex w-[101.333px] min-h-[132px] flex-col items-center gap-2 p-3 rounded-2xl bg-black/65 shadow-[0_2px_4px_0_rgba(0,0,0,0.15)] backdrop-blur-[16px]";

                                    return (
                                        <DefaultContentContainer
                                            key={achievement.documentId}
                                            className={cellClassName}
                                            rounded="md"
                                        >
                                            {achievement.image ? (
                                                <StrapiMedia
                                                    size="md" // does not matter for svgs, but is required
                                                    image={achievement.image}
                                                    alt={achievement.name}
                                                    className="w-full h-auto"
                                                    objectFit="contain"
                                                    useImageAspectRatio={false}
                                                />
                                            ) : (
                                                <AchievementBadgePlaceholder className="w-full h-auto flex-shrink-0 opacity-70" />
                                            )}
                                            <div className="flex flex-col gap-0 overflow-hidden w-full">
                                                <div className="text-white text-xs font-semibold text-center leading-tight wrap-break-word">
                                                    {achievement.name}
                                                </div>
                                            </div>
                                        </DefaultContentContainer>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </PopupContainer>
            )}
        </AnimatePresence>
    );
};

export default Achievements;